import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronUpIcon } from '@/components/icons';
import type { CartStatus, CartItemResponse, CartResponse } from '@/features/cart/types';
import { CartStatus as CartStatusEnum } from '@/features/cart/types';
import { calculateTotalsByCurrency } from '@/features/cart/utils/cartUtils';
import { formatCurrencyForDisplay } from '@/lib/utils';



interface FloatingCheckoutButtonProps {
  cartStatus: CartStatus;
  canProceed: boolean;
  isStatusesLoading: boolean;
  isTimerExpired: boolean;
  isCheckoutProcessing: boolean;
  onCheckout: () => void;
  items: CartItemResponse[];
  cart: CartResponse | null;
  timeRemaining?: number | null;
  formattedTime?: string;
  timerIsExpired?: boolean;
  onCancelCheckout?: () => void;
  isCancelingCheckout?: boolean;
  isCooldownActive?: boolean;
}

const FloatingCheckoutButton: React.FC<FloatingCheckoutButtonProps> = ({
  cartStatus,
  canProceed,
  isStatusesLoading,
  isTimerExpired,
  isCheckoutProcessing,
  onCheckout,
  items,
  cart,
  timeRemaining = null,
  formattedTime = '',
  timerIsExpired = false,
  onCancelCheckout,
  isCancelingCheckout = false,
  isCooldownActive = false,
}) => {
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  /* replace formatPrice and calculations */
  const formatPrice = (amount: number, currency: string) => formatCurrencyForDisplay(amount, currency);
  
  const totalsByCurrency = calculateTotalsByCurrency(items);
  // Use cart's currency as the main/primary currency
  const cartCurrency = cart?.currency || '';
  // Determine main currency to display in collapsed view (use cart currency or first available)
  const mainCurrency = cartCurrency || (items.length > 0 ? items[0].currency : '');
  if (!mainCurrency && items.length > 0) {
    console.warn('FloatingCheckoutButton: currency missing from cart and items');
  }
  const mainTotals = totalsByCurrency[mainCurrency] || { total: 0 };
  const currencyCount = Object.keys(totalsByCurrency).length;
  
  // Sort currencies so cart currency comes first, then others alphabetically
  const sortedCurrencies = Object.keys(totalsByCurrency).sort((a, b) => {
    if (a === cartCurrency) return -1;
    if (b === cartCurrency) return 1;
    return a.localeCompare(b);
  });

  const isCompleted = cartStatus === CartStatusEnum.Completed || cartStatus === CartStatusEnum.SuccessfulPayment;

  // Hide completely if cart is completed
  if (isCompleted) {
    return null;
  }

  const isDisabled = !canProceed || isCheckoutProcessing || (cartStatus === CartStatusEnum.PendingPayment && isTimerExpired);

  // Check if timer should be visible (only when not expired)
  const showTimer = !timerIsExpired && timeRemaining !== null && formattedTime !== '';
  
  // Show proceed to payment button only when timer is not expired
  const showProceedButton = !timerIsExpired;
  
  // Show cancel checkout button when there's an active payment session (even if expired)
  const showCancelButton = onCancelCheckout && (cartStatus === CartStatusEnum.PendingPayment || cartStatus === CartStatusEnum.FailedPayment);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.7 }}
      className="fixed bottom-8 right-8 z-50"
    >
      <div className="relative w-80">
        {/* Order Summary Card */}
        <motion.div
          layout
          className="bg-black/80 backdrop-blur-md border border-gray-600/50 rounded-2xl shadow-2xl overflow-hidden w-full"
        >
          {/* Summary Header - Always Visible */}
          <motion.div
            layout
            onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
            className="px-6 py-4 flex items-center space-x-4 cursor-pointer hover:bg-gray-800/50 transition-colors"
          >
            <div className="text-right flex-1">
              <div className="text-2xl font-bold text-white">
                {formatPrice(mainTotals.total, mainCurrency)}
                {currencyCount > 1 && <span className="text-xs text-gray-400 ml-1">(+ others)</span>}
              </div>
              <div className="text-sm text-gray-300">
                Order Summary
              </div>
            </div>
            <motion.div
              animate={{ rotate: isSummaryExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
              className="w-8 h-8 rounded-full bg-gray-700/80 hover:bg-gray-600 flex items-center justify-center transition-all duration-200"
            >
              <ChevronUpIcon />
            </motion.div>
          </motion.div>

          {/* Expandable Summary Details */}
          <AnimatePresence>
            {isSummaryExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="border-t border-gray-700/50"
              >
                <div className="px-6 py-4 space-y-4 max-h-[50vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
                  <div>
                    <h3 className="text-lg font-bold text-white mb-3">Order Summary</h3>
                    
                    {sortedCurrencies.map((currency, index) => {
                      const totals = totalsByCurrency[currency];
                      const isCartCurrency = currency === cartCurrency;
                      return (
                      <div key={currency} className={index > 0 ? "mt-6 pt-6 border-t border-gray-700/50" : ""}>
                         {sortedCurrencies.length > 1 && (
                            <h4 className="text-gray-400 font-medium mb-2 text-sm">
                              {currency} Items{isCartCurrency}
                            </h4>
                         )}
                         {/* Subtotal */}
                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
                          <span className="text-gray-300">Subtotal</span>
                          <span className="text-white font-medium">
                            {formatPrice(totals.subtotal, currency)}
                          </span>
                        </div>

                        {/* Platform Fees */}
                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
                          <span className="text-gray-300">Platform Fees</span>
                          <span className="text-white font-medium">
                            {formatPrice(totals.platformFee, currency)}
                          </span>
                        </div>

                        {/* Total */}
                        <div className="flex justify-between items-center py-3 border-t border-gray-600/50">
                          <span className="text-lg font-bold text-white">Total</span>
                          <span className="text-2xl font-bold text-blue-400">
                            {formatPrice(totals.total, currency)}
                          </span>
                        </div>
                      </div>
                    );})}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Timer - Between dropdown and button (only when not expired) */}
        {showTimer && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-4 w-full"
          >
            <div className="bg-black/60 backdrop-blur-md border border-gray-600/50 rounded-xl px-4 py-3 shadow-lg">
              <div className="text-center">
                <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">
                  Pay within
                </p>
                <div className="relative inline-block mb-1">
                  <div className="absolute inset-0 bg-blue-500/20 blur-xl rounded-full"></div>
                  <p className="relative text-white font-mono font-bold text-xl tracking-wider">
                    {formattedTime}
                  </p>
                </div>
                {timeRemaining !== null && timeRemaining < 60 && (
                  <p className="text-yellow-400 text-xs mt-1 font-medium">
                    Less than a minute left
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Proceed to Payment Button (only when timer not expired) */}
        {showProceedButton && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            onClick={onCheckout}
            disabled={isDisabled}
            className={`mt-4 w-full px-6 py-3 rounded-xl shadow-2xl flex items-center justify-center space-x-2 transition-all duration-200 border ${
              isDisabled
                ? 'bg-gray-600 hover:bg-gray-600 text-gray-400 border-gray-500/50 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white border-blue-500/50'
            }`}
          >
            {isCheckoutProcessing ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span className="font-semibold">Processing...</span>
              </>
            ) : cartStatus === CartStatusEnum.PendingPayment && isTimerExpired ? (
              <span className="font-semibold">Checkout session expired</span>
            ) : isStatusesLoading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : isCooldownActive && cartStatus === CartStatusEnum.Active ? (
              <span className="font-semibold">Proceed to Payment</span>
            ) : canProceed ? (
              <span className="font-semibold">Proceed to Payment</span>
            ) : (
              <span className="font-semibold">Please resolve all issues before proceeding</span>
            )}
          </motion.button>
        )}

        {/* Cancel Checkout Button - Below proceed button (or below order summary if timer expired) */}
        {showCancelButton && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            onClick={onCancelCheckout}
            disabled={isCancelingCheckout}
            className={`mt-4 w-full px-6 py-3 rounded-xl shadow-2xl flex items-center justify-center space-x-2 transition-all duration-200 border bg-yellow-600 hover:bg-yellow-700 text-white border-yellow-500/50 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isCancelingCheckout ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="font-semibold">Canceling...</span>
              </>
            ) : (
              <span className="font-semibold">Cancel Checkout</span>
            )}
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

export default FloatingCheckoutButton;

